import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money } from "@/lib/api";
import { StatusPill, InlineLoader } from "@/components/shared";
import { StatCard } from "@/pages/organizer/OrganizerDashboard";
import ProManager from "@/components/pro/ProManager";
import { Button } from "@/components/ui/button";
import { HeartHandshake, DollarSign, Megaphone, BadgeCheck, ArrowRight } from "lucide-react";

export default function NonprofitDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/nonprofits/dashboard").then((r) => setData(r.data)).catch(() => setData({ verified: false, verification: { status: "none" } }));
  }, []);

  if (!data) return <InlineLoader label="Loading" />;

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ff-blue sm:text-3xl">Nonprofit Dashboard</h1>
          <p className="mt-1 text-sm text-ff-medium">Basic receipt support in v1 for verified nonprofits.</p>
        </div>
        {!data.verified && (
          <Button asChild className="bg-ff-green font-semibold text-white hover:bg-ff-green/90">
            <Link to="/nonprofits/verify" data-testid="np-dash-verify">Get verified</Link>
          </Button>
        )}
      </div>

      <div className="mb-6 rounded-2xl border border-ff-border bg-white p-6 shadow-sm" data-testid="np-verification-card">
        <div className="flex items-center gap-3">
          <span className={`flex h-12 w-12 items-center justify-center rounded-lg ${data.verified ? "bg-[#ECFDF3]" : "bg-[#F3F4F6]"}`}>
            {data.verified ? <BadgeCheck className="h-6 w-6 text-ff-green" /> : <HeartHandshake className="h-6 w-6 text-ff-medium" />}
          </span>
          <div>
            <div className="font-heading text-lg font-bold text-ff-blue">
              {data.verified ? "Nonprofit Verified" : "Not yet verified"}
            </div>
            <div className="mt-0.5 text-sm text-ff-medium">
              {data.verification?.status && data.verification.status !== "none"
                ? <>Status: <StatusPill status={data.verification.status} /></>
                : "Submit your organization details to earn the verified badge."}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard icon={DollarSign} label="Total Raised" value={money(data.total_raised)} accent="green" />
        <StatCard icon={Megaphone} label="Nonprofit Campaigns" value={data.campaign_count || 0} />
        <StatCard icon={BadgeCheck} label="Verified" value={data.verified ? "Yes" : "No"} accent="red" />
      </div>

      <div className="mt-8 rounded-2xl border border-ff-border bg-ff-blue p-8 text-white">
        <h2 className="font-heading text-xl font-bold">Run a nonprofit campaign</h2>
        <p className="mt-2 max-w-lg text-white/70">Start a fundraiser marked as nonprofit. Once verified, the Nonprofit Verified badge and receipt support apply automatically.</p>
        <Button asChild className="mt-5 bg-white font-semibold text-ff-blue hover:bg-white/90">
          <Link to="/start-a-fundraiser" data-testid="np-dash-start">Start a nonprofit campaign <ArrowRight className="ml-2 h-4 w-4" /></Link>
        </Button>
      </div>

      <ProManager />
    </div>
  );
}
